import { ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { EditActions, EditButton, Panel } from '../Panel';

interface Props {
  cameras: string[];
  originalCameras: string[];
  saving?: boolean;
  onSave: (next: string[], onSaved: () => void) => void;
}

const SINGLE_COLUMN_MAX = 5;

export function cameraColumns(names: string[]): string[][] {
  if (names.length <= SINGLE_COLUMN_MAX) {
    return [names];
  }
  const first =
    names.length > SINGLE_COLUMN_MAX * 2
      ? Math.ceil(names.length / 2)
      : SINGLE_COLUMN_MAX;
  return [names.slice(0, first), names.slice(first)];
}

export function CamerasCard({
  cameras,
  originalCameras,
  saving = false,
  onSave,
}: Props) {
  const rows = [
    ...originalCameras,
    ...cameras.filter((camera) => !originalCameras.includes(camera)),
  ];
  const [editing, setEditing] = useState(false);
  const [kept, setKept] = useState<string[]>(cameras);

  const toggle = (camera: string) =>
    setKept((prev) =>
      prev.includes(camera)
        ? prev.filter((name) => name !== camera)
        : [...prev, camera]
    );

  const startEdit = () => {
    setKept(cameras);
    setEditing(true);
  };

  const save = () =>
    onSave(
      rows.filter((camera) => kept.includes(camera)),
      () => setEditing(false)
    );

  return (
    <Panel
      title="Cameras"
      action={!editing && <EditButton label="Cameras" onClick={startEdit} />}
    >
      {(editing ? rows : cameras).length === 0 ? (
        <div className="text-onlight-text-2 dark:text-ondark-text-2 mt-2 text-sm">
          -
        </div>
      ) : (
        <div
          className={`mt-2 ${
            cameraColumns(editing ? rows : cameras).length > 1
              ? 'grid grid-cols-2 gap-x-4'
              : ''
          }`}
        >
          {cameraColumns(editing ? rows : cameras).map((column, index) => (
            <ul key={index} className="space-y-1">
              {column.map((camera) => (
                <CameraRow
                  key={camera}
                  camera={camera}
                  removed={editing && !kept.includes(camera)}
                  editing={editing}
                  onToggle={() => toggle(camera)}
                />
              ))}
            </ul>
          ))}
        </div>
      )}

      {editing && (
        <EditActions
          saving={saving}
          onSave={save}
          onCancel={() => setEditing(false)}
        />
      )}
    </Panel>
  );
}

interface RowProps {
  camera: string;
  removed: boolean;
  editing: boolean;
  onToggle: () => void;
}

function CameraRow({ camera, removed, editing, onToggle }: RowProps) {
  const Icon = removed ? ArrowUturnLeftIcon : XMarkIcon;
  return (
    <li
      className={`flex items-center justify-between gap-2 rounded ${
        editing ? 'px-3 py-1.5' : ''
      } ${
        removed
          ? 'bg-red-50 dark:bg-red-900/30'
          : editing
          ? 'dark:bg-ondark-bg-2 bg-onlight-bg-2'
          : ''
      }`}
    >
      <span
        className={`truncate text-sm font-medium ${
          removed
            ? 'text-onlight-text-2 dark:text-ondark-text-2 line-through'
            : 'text-onlight-text-1 dark:text-ondark-text-1'
        }`}
      >
        {camera}
      </span>
      {editing && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={`${removed ? 'Restore' : 'Remove'} ${camera}`}
          className={`shrink-0 rounded p-0.5 ${
            removed
              ? 'text-onlight-text-2 dark:text-ondark-text-2'
              : 'text-red-600 dark:text-red-400'
          }`}
        >
          <Icon className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

export default CamerasCard;
